/**
 * Copie la structure de répartition UE de l'option courante vers une ou
 * plusieurs options cibles (mêmes matières par nom). N'écrase AUCUNE note —
 * met seulement à jour le rattachement matière → UE.
 *
 * Flow :
 *   1. L'utilisateur choisit les options cibles (multi-checkbox).
 *   2. Bouton "Prévisualiser" → backend renvoie pour chaque cible le
 *      nombre d'UE/matières appariées + la liste des matières sans homonyme.
 *   3. Bouton "Appliquer" → exécute la copie (atomique, sans toucher notes).
 */
import React, { useState, useMemo } from 'react';
import { X, Copy, Loader, CheckCircle2, AlertTriangle, Info, Filter } from 'lucide-react';
import {
  useCopierRepartitionVersOptionsMutation,
  CopierRepartitionResponse,
} from '../../services/uesApi';

interface Option {
  id: number;
  nom: string;
  code?: string;
  // Fix 2026-09-09 : champs pour filtrage
  pole_nom?: string;
  niveau?: string;
  parcours_nom?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  optionSource: { id: number; nom: string } | null;
  niveau: string;
  semestre: number;
  optionsCandidates: Option[]; // mêmes niveau + actives
}

const CopierVersOptionsModal: React.FC<Props> = ({
  open, onClose, optionSource, niveau, semestre, optionsCandidates,
}) => {
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [preview, setPreview] = useState<CopierRepartitionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const [copier, { isLoading }] = useCopierRepartitionVersOptionsMutation();

  // Fix 2026-09-09 : filtres pôle / niveau / FI-CS / parcours
  const [filtrePole, setFiltrePole] = useState('');
  const [filtreNiveau, setFiltreNiveau] = useState('');
  const [filtreParcours, setFiltreParcours] = useState('');
  const [filtreTypeFormation, setFiltreTypeFormation] = useState<'' | 'FI' | 'CS'>('');
  const [recherche, setRecherche] = useState('');

  const ciblesBase = useMemo(
    () => optionsCandidates.filter(o => o.id !== optionSource?.id),
    [optionsCandidates, optionSource],
  );

  // Listes uniques pour les selects (extraites des options candidates)
  const polesDispo = useMemo(() => {
    const s = new Set<string>();
    ciblesBase.forEach(o => { if (o.pole_nom) s.add(o.pole_nom); });
    return Array.from(s).sort();
  }, [ciblesBase]);
  const niveauxDispo = useMemo(() => {
    const s = new Set<string>();
    ciblesBase.forEach(o => { if (o.niveau) s.add(o.niveau); });
    return Array.from(s).sort();
  }, [ciblesBase]);
  const parcoursDispo = useMemo(() => {
    const s = new Set<string>();
    ciblesBase.forEach(o => { if (o.parcours_nom) s.add(o.parcours_nom); });
    return Array.from(s).sort();
  }, [ciblesBase]);

  const cibles = useMemo(() => {
    let list = ciblesBase;
    if (filtrePole) list = list.filter(o => o.pole_nom === filtrePole);
    if (filtreNiveau) list = list.filter(o => o.niveau === filtreNiveau);
    if (filtreParcours) list = list.filter(o => (o.parcours_nom || '') === filtreParcours);
    if (filtreTypeFormation === 'CS') {
      list = list.filter(o => o.nom.includes('(CS)') || (o.code || '').endsWith('-CS'));
    } else if (filtreTypeFormation === 'FI') {
      list = list.filter(o => !o.nom.includes('(CS)') && !(o.code || '').endsWith('-CS'));
    }
    if (recherche.trim()) {
      const q = recherche.trim().toLowerCase();
      list = list.filter(o => o.nom.toLowerCase().includes(q) || (o.code || '').toLowerCase().includes(q));
    }
    return list;
  }, [ciblesBase, filtrePole, filtreNiveau, filtreParcours, filtreTypeFormation, recherche]);

  if (!open || !optionSource) return null;

  const toggle = (id: number) => {
    setSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setPreview(null);
    setApplied(false);
  };

  const handlePreview = async () => {
    setError(null);
    setApplied(false);
    try {
      const resp = await copier({
        option_source_id: optionSource.id,
        options_cibles_ids: Array.from(selection),
        niveau,
        semestre,
        dry_run: true,
      }).unwrap();
      setPreview(resp);
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur lors de la prévisualisation');
    }
  };

  const handleAppliquer = async () => {
    setError(null);
    try {
      const resp = await copier({
        option_source_id: optionSource.id,
        options_cibles_ids: Array.from(selection),
        niveau,
        semestre,
        dry_run: false,
      }).unwrap();
      setPreview(resp);
      setApplied(true);
    } catch (e: any) {
      setError(e?.data?.error || e?.message || 'Erreur lors de l\'application');
    }
  };

  const handleClose = () => {
    setSelection(new Set());
    setPreview(null);
    setError(null);
    setApplied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Copy size={20} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Copier la répartition vers d'autres options
            </h2>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm text-blue-900 dark:text-blue-100">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <div>
              Copie la structure UE depuis <strong>{optionSource.nom}</strong>
              {' '}({niveau}-S{semestre}) vers les options choisies.
              Les matières sont appariées <strong>par nom identique</strong>.
              <br />
              <strong>Aucune note n'est modifiée</strong> — seul le rattachement matière → UE change.
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Options cibles ({selection.size} sélectionnée{selection.size > 1 ? 's' : ''} · {cibles.length} visible{cibles.length > 1 ? 's' : ''} sur {ciblesBase.length})
            </label>

            {/* Fix 2026-09-09 : barre de filtres */}
            <div className="mb-2 p-2 rounded bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-gray-500">
                <Filter size={11} /> Filtres
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <select value={filtrePole} onChange={(e) => setFiltrePole(e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
                  <option value="">Tous pôles</option>
                  {polesDispo.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={filtreNiveau} onChange={(e) => setFiltreNiveau(e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
                  <option value="">Tous niveaux</option>
                  {niveauxDispo.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <select value={filtreParcours} onChange={(e) => setFiltreParcours(e.target.value)}
                  className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
                  <option value="">Tous parcours</option>
                  {parcoursDispo.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={filtreTypeFormation} onChange={(e) => setFiltreTypeFormation(e.target.value as any)}
                  className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800">
                  <option value="">FI + CS</option>
                  <option value="FI">FI</option>
                  <option value="CS">CS</option>
                </select>
                <input type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)}
                  placeholder="Rechercher…"
                  className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800" />
              </div>
              <div className="flex gap-2 text-[10px]">
                <button type="button"
                  onClick={() => setSelection(prev => {
                    const next = new Set(prev);
                    cibles.forEach(o => next.add(o.id));
                    return next;
                  })}
                  className="text-blue-600 hover:underline">Cocher les {cibles.length} visibles</button>
                <button type="button"
                  onClick={() => setSelection(prev => {
                    const next = new Set(prev);
                    cibles.forEach(o => next.delete(o.id));
                    return next;
                  })}
                  className="text-gray-500 hover:underline">Décocher les visibles</button>
                {(filtrePole || filtreNiveau || filtreParcours || filtreTypeFormation || recherche) && (
                  <button type="button"
                    onClick={() => { setFiltrePole(''); setFiltreNiveau(''); setFiltreParcours(''); setFiltreTypeFormation(''); setRecherche(''); }}
                    className="ml-auto text-amber-600 hover:underline">Réinitialiser les filtres</button>
                )}
              </div>
            </div>

            <div className="border border-gray-300 dark:border-gray-600 rounded max-h-64 overflow-y-auto">
              {cibles.length === 0 ? (
                <div className="p-4 text-sm text-gray-500 text-center">
                  Aucune option candidate pour {niveau}{ciblesBase.length > 0 ? ' avec ces filtres' : ''}.
                </div>
              ) : cibles.map(opt => (
                <label
                  key={opt.id}
                  className={`flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700 last:border-b-0 cursor-pointer transition ${
                    selection.has(opt.id)
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selection.has(opt.id)}
                    onChange={() => toggle(opt.id)}
                    className="rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-900 dark:text-gray-100 truncate">{opt.nom}</div>
                    <div className="text-[10px] text-gray-500 flex flex-wrap gap-1.5 mt-0.5">
                      {opt.niveau && <span className="px-1 rounded bg-slate-100 dark:bg-gray-700">{opt.niveau}</span>}
                      {opt.pole_nom && <span className="px-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{opt.pole_nom}</span>}
                      {opt.parcours_nom && <span className="px-1 rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">{opt.parcours_nom}</span>}
                      {(opt.nom.includes('(CS)') || (opt.code || '').endsWith('-CS')) && (
                        <span className="px-1 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-semibold">CS</span>
                      )}
                    </div>
                  </div>
                  {opt.code && (
                    <span className="text-xs font-mono text-gray-500 ml-auto flex-shrink-0">{opt.code}</span>
                  )}
                </label>
              ))}
            </div>
          </div>

          {preview && (
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                {applied ? 'Résultat de la copie :' : 'Prévisualisation :'}
              </div>
              {preview.resume_par_option.map(r => (
                <div
                  key={r.option_id}
                  className="border border-gray-200 dark:border-gray-700 rounded p-3 bg-gray-50 dark:bg-gray-900/40"
                >
                  <div className="flex items-center gap-2 mb-1">
                    {applied ? (
                      <CheckCircle2 size={14} className="text-green-600" />
                    ) : (
                      <Info size={14} className="text-blue-600" />
                    )}
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {r.option_nom}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 ml-5">
                    {r.nb_ues} UE · {r.nb_matieres_apparies} matière{r.nb_matieres_apparies > 1 ? 's' : ''} appariée{r.nb_matieres_apparies > 1 ? 's' : ''}
                  </div>
                  {r.non_apparies.length > 0 && (
                    <div className="ml-5 mt-1 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1">
                      <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                      <div>
                        {r.non_apparies.length} matière{r.non_apparies.length > 1 ? 's' : ''} sans homonyme :
                        {' '}
                        {r.non_apparies.slice(0, 3).map(n => n.matiere_source_nom).join(', ')}
                        {r.non_apparies.length > 3 && ` …+${r.non_apparies.length - 3}`}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-900 dark:text-red-100">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 rounded-b-lg">
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            {applied ? 'Fermer' : 'Annuler'}
          </button>
          {!applied && (
            <>
              <button
                onClick={handlePreview}
                disabled={selection.size === 0 || isLoading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white text-sm font-medium rounded"
              >
                {isLoading && !preview ? <Loader size={16} className="animate-spin" /> : <Info size={16} />}
                Prévisualiser
              </button>
              <button
                onClick={handleAppliquer}
                disabled={selection.size === 0 || isLoading || !preview}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-medium rounded"
              >
                {isLoading && preview ? <Loader size={16} className="animate-spin" /> : <Copy size={16} />}
                Appliquer la copie
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CopierVersOptionsModal;
